import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import moment from 'moment';

import { postApprovalRejected } from '../../../api/approvalsApi';

export default function RejectAppointmentModal({ toggle, data, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState([]);

  const columns = useMemo(
    () => [
      {
        field: 'label',
        headerName: 'MD Name',
        flex: 1,
      },
      {
        field: 'date_scheduled',
        headerName: 'Appointment Date',
        flex: 1,
        renderCell: ({ row }) => moment(row.date_time_scheduled).format('DD/MM/YYYY'),
      },
      {
        field: 'date_time_scheduled',
        headerName: 'Appointment Time',
        minWidth: 250,
        flex: 1,
        renderCell: ({ row }) => moment(`${row.date_time_scheduled}`).format('hh:mm A'),
      },
    ],
    [data],
  );

  const handleReason = (e) => {
    setReason(e.target.value);
  };

  const handleClose = () => {
    setLoading(false);
    setReason('');
    setSelected([]);

    onClose();
  };

  const handleSubmit = async () => {
    if (!selected.length) {
      await Swal.fire({
        title: 'Approvals',
        text: 'Please select appointment first.',
        icon: 'error',
      });

      return;
    }

    if (reason === '') {
      await Swal.fire({
        title: 'Approvals',
        text: 'The reason field is required.',
        icon: 'error',
      });

      return;
    }

    try {
      toast.loading('Please wait ...');
      setLoading(true);

      const res = await postApprovalRejected({
        approved_appointment_ids: [],
        reject_appointment_ids: selected,
        reason,
      });

      setLoading(false);
      toast.dismiss();

      const { success, message } = res.data;

      if (!success) {
        toast.error(message);
        return;
      }

      toast.success(message);
      onSuccess();
      handleClose();
    } catch (error) {
      toast.dismiss();
      setLoading(false);
      toast.error(error.message);
    }
  };

  const handleOnSelectionModelChange = (appointmentIds) => {
    setSelected(appointmentIds);
  };

  return (
    <Dialog onClose={handleClose} open={toggle} fullWidth maxWidth='md'>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        DISAPPROVAL/REJECTION
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ height: '500px', marginBottom: '15px' }}>
          <DataGrid
            rows={data}
            rowCount={data.length}
            columns={columns}
            pageSize={100}
            getRowId={(row) => row.appointment_id}
            disableSelectionOnClick
            checkboxSelection
            onSelectionModelChange={handleOnSelectionModelChange}
          />
        </div>
        <TextField
          id='reason'
          label='Reason for Disapproval'
          maxRows={10}
          multiline
          fullWidth
          value={reason}
          onChange={handleReason}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='contained' color='success' onClick={handleSubmit} disabled={loading}>
          Submit
        </Button>
        <Button variant='contained' color='error' onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RejectAppointmentModal.defaultProps = {
  toggle: false,
  data: [],
  onClose: () => {},
  onSuccess: () => {},
};

RejectAppointmentModal.propTypes = {
  toggle: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      date_scheduled: PropTypes.string.isRequired,
      time_scheduled: PropTypes.string.isRequired,
      appointment_id: PropTypes.string.isRequired,
    }),
  ),
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};
